import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './InvoiceStyles.css';
import { PrintHeader, PrintFooter } from '../common/PrintTemplate';
import PrintInvoice from './PrintInvoice';
const API_URL = import.meta.env.VITE_API_URL || '';

const InvoiceModule = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [printInvoice, setPrintInvoice] = useState(null);
  
  const [formData, setFormData] = useState({
    clientName: '',
    customerPin: '',
    location: '',
    items: [{ description: '', quantity: 1, rate: 0 }],
    taxRate: '',
    dueDate: '',
    notes: '',
    status: 'Draft'
  });

  useEffect(() => { fetchInvoices(); }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data.data || []);
    } catch (error) { console.error('Error:', error); }
    setLoading(false);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => setFormData(prev => ({ ...prev, items: [...prev.items, { description: '', quantity: 1, rate: 0 }] }));
  
  const removeItem = (index) => {
    if (formData.items.length > 1) setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (parseFloat(item.quantity) * parseFloat(item.rate) || 0), 0);
    const taxRate = parseFloat(formData.taxRate) || 0;
    const taxAmount = (subtotal * taxRate) / 100;
    return { subtotal, taxAmount, total: subtotal + taxAmount };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { subtotal, taxAmount, total } = calculateTotals();
      const invoiceData = { ...formData, subtotal, taxAmount, totalAmount: total };

      if (editingInvoice) {
        await axios.put(`${API_URL}/invoices/${editingInvoice._id}`, invoiceData, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API_URL}/invoices`, invoiceData, { headers: { Authorization: `Bearer ${token}` } });
      }
      setShowModal(false); setEditingInvoice(null); resetForm(); fetchInvoices();
    } catch (error) { alert('Failed to save invoice'); }
    setLoading(false);
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      clientName: invoice.clientName,
      customerPin: invoice.customerPin,
      companyPin: invoice.companyPin || '',
      location: invoice.location || '',
      items: invoice.items,
      taxRate: invoice.taxRate,
      dueDate: invoice.dueDate ? invoice.dueDate.split('T')[0] : '',
      notes: invoice.notes || '',
      status: invoice.status
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this invoice?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/invoices/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchInvoices();
    } catch (error) { alert('Failed to delete'); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/invoices/${id}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      fetchInvoices();
    } catch (error) { console.error('Error:', error); }
  };

  const viewInvoiceDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailModal(true);
  };

  const resetForm = () => setFormData({
    clientName: '', customerPin: '', companyPin: '', location: '', items: [{ description: '', quantity: 1, rate: 0 }],
    taxRate: '', dueDate: '', notes: '', status: 'Draft'
  });

  const formatCurrency = (amount) => new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(amount);
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-KE');
  const { subtotal, taxAmount, total } = calculateTotals();

  return (
    <div className="invoice-module">
      <div className="module-header">
        <h2>Invoice Management</h2>
        <button className="btn btn-primary" onClick={() => { setEditingInvoice(null); resetForm(); setShowModal(true); }}>+ New Invoice</button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Client Name</th>
              <th>Customer PIN</th>
              <th>Location</th>
              <th>Items</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Issued By</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="10" className="text-center">Loading...</td></tr> :
             invoices.length === 0 ? <tr><td colSpan="10" className="text-center">No invoices</td></tr> :
             invoices.map(inv => (
              <tr key={inv._id}>
                <td>{inv.invoiceNumber}</td>
                <td>{inv.clientName}</td>
                <td>{inv.customerPin}</td>
                <td>{inv.location || '-'}</td>
                <td>{inv.items.length} item(s)</td>
                <td>{formatDate(inv.dateIssued)}</td>
                <td>{formatCurrency(inv.totalAmount)}</td>
                <td>{inv.issuedBy}</td>
                <td>
                  <select value={inv.status} onChange={(e) => handleStatusChange(inv._id, e.target.value)} className={`status-select status-${inv.status.toLowerCase()}`}>
                    <option value="Draft">Draft</option>
                    <option value="Sent">Sent</option>
                    <option value="Paid">Paid</option>
                    <option value="Overdue">Overdue</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </td>
                <td className="actions">
                  <button className="btn-icon" onClick={() => viewInvoiceDetails(inv)} title="View">👁️</button>
                  <button className="btn-icon" onClick={() => setPrintInvoice(inv)} title="Print">🖨️</button>
                  <button className="btn-icon" onClick={() => handleEdit(inv)} title="Edit">✏️</button>
                  <button className="btn-icon" onClick={() => handleDelete(inv._id)} title="Delete">🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>{editingInvoice ? 'Edit' : 'New'} Invoice</h3><button className="btn-close" onClick={() => setShowModal(false)}>×</button></div>
            <form onSubmit={handleSubmit} className="invoice-form">
              <div className="form-row">
                <div className="form-group"><label>Client Name *</label><input type="text" value={formData.clientName} onChange={e => setFormData({...formData, clientName: e.target.value})} required /></div>
                <div className="form-group"><label>Customer PIN *</label><input type="text" value={formData.customerPin} onChange={e => setFormData({...formData, customerPin: e.target.value})} required /></div>
                <div className="form-group"><label>Gismac PIN</label><input type="text" value={formData.companyPin} onChange={e => setFormData({...formData, companyPin: e.target.value})} placeholder="Gismac Company PIN" /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Location</label><input type="text" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} placeholder="Site/Branch location" /></div>
                <div className="form-group"><label>Due Date</label><input type="date" value={formData.dueDate} onChange={e => setFormData({...formData, dueDate: e.target.value})} /></div>
                <div className="form-group"><label>Tax Type</label><select value={formData.taxRate} onChange={e => setFormData({...formData, taxRate: e.target.value})}>
                  <option value="">Select Tax Type</option>
                  <option value="0">Non-Taxable</option>
                  <option value="8">8% VAT</option>
                  <option value="16">16% VAT</option>
                  <option value="exempted">Exempted</option>
                </select></div>
              </div>
              
              <div className="items-section">
                <h4>Line Items (Description | Quantity | Rate | Amount)</h4>
                {formData.items.map((item, idx) => (
                  <div key={idx} className="item-row">
                    <input type="text" placeholder="Item description" value={item.description} onChange={e => handleItemChange(idx, 'description', e.target.value)} required />
                    <input type="number" placeholder="Qty" value={item.quantity} onChange={e => handleItemChange(idx, 'quantity', parseFloat(e.target.value)||0)} min="1" required />
                    <input type="number" placeholder="Rate (KES)" value={item.rate} onChange={e => handleItemChange(idx, 'rate', parseFloat(e.target.value)||0)} min="0" step="0.01" required />
                    <span className="item-amount">{formatCurrency(item.quantity * item.rate)}</span>
                    <button type="button" className="btn-remove" onClick={() => removeItem(idx)} disabled={formData.items.length===1}>×</button>
                  </div>
                ))}
                <button type="button" className="btn-add-item" onClick={addItem}>+ Add Item</button>
              </div>

              <div className="totals-section">
                <div className="total-row"><span>Subtotal:</span><span>{formatCurrency(subtotal)}</span></div>
                <div className="total-row"><span>Tax ({formData.taxRate ? formData.taxRate + "%" : "Non-Taxable"}):</span><span>{formatCurrency(taxAmount)}</span></div>
                <div className="total-row grand-total"><span>Total Amount:</span><span>{formatCurrency(total)}</span></div>
              </div>

              <div className="form-group"><label>Notes</label><textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} rows="2" placeholder="Additional notes..." /></div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : (editingInvoice ? 'Update' : 'Create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showDetailModal && selectedInvoice && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Invoice Details - {selectedInvoice.invoiceNumber}</h3>
              <button className="btn-close" onClick={() => setShowDetailModal(false)}>×</button>
            </div>
            <div className="invoice-details">
              <div className="detail-row"><strong>Client:</strong> {selectedInvoice.clientName}</div>
              <div className="detail-row"><strong>Customer PIN:</strong> {selectedInvoice.customerPin}</div>
              <div className="detail-row"><strong>Location:</strong> {selectedInvoice.location || 'N/A'}</div>
              <div className="detail-row"><strong>Date Issued:</strong> {formatDate(selectedInvoice.dateIssued)}</div>
              <div className="detail-row"><strong>Due Date:</strong> {selectedInvoice.dueDate ? formatDate(selectedInvoice.dueDate) : 'N/A'}</div>
              <div className="detail-row"><strong>Issued By:</strong> accounts@gismac.co.ke</div>
              <div className="detail-row"><strong>Status:</strong> <span className={`status-badge status-${selectedInvoice.status.toLowerCase()}`}>{selectedInvoice.status}</span></div>
              
              <h4>Items List:</h4>
              <table className="items-table">
                <thead><tr><th>#</th><th>Description</th><th>Quantity</th><th>Rate (KES)</th><th>Amount (KES)</th></tr></thead>
                <tbody>
                  {selectedInvoice.items.map((item, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{item.description}</td>
                      <td>{item.quantity}</td>
                      <td>{formatCurrency(item.rate)}</td>
                      <td>{formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="detail-totals">
                <div className="detail-total-row"><span>Subtotal:</span><span>{formatCurrency(selectedInvoice.subtotal)}</span></div>
                <div className="detail-total-row"><span>Tax ({selectedInvoice.taxRate}%):</span><span>{formatCurrency(selectedInvoice.taxAmount)}</span></div>
                <div className="detail-total-row grand"><span>Total:</span><span>{formatCurrency(selectedInvoice.totalAmount)}</span></div>
              </div>

              {selectedInvoice.notes && <div className="detail-notes"><strong>Notes:</strong> {selectedInvoice.notes}</div>}
            </div>
          </div>
        </div>
      )}
     {printInvoice && (
  <PrintInvoice 
    invoice={printInvoice} 
    onClose={() => setPrintInvoice(null)} 
  />
)}
    </div>
  );
};

export default InvoiceModule;
